// --- Expression Builder Constants ---

/**
 * Operators available for different field types
 * @author Abhishek Dhillon
 */
export const EXPRESSION_OPERATORS = {
  STRING: [
    { id: "CONTAINS", label: "contains", type: "operator" },
    { id: "MATCHES_WITH", label: "matches with", type: "operator" },
    { id: "STARTS_WITH", label: "starts with", type: "operator" },
    { id: "IS_BLANK", label: "is blank", type: "standard" },
    { id: "IS_NOT_BLANK", label: "is not blank", type: "standard" },
  ],
  NUMBER: [
    { id: "EQUALS", label: "= (equal to)", type: "operator" },
    { id: "NOT_EQUALS", label: "!= (not equal to)", type: "operator" },
    { id: "GREATER_THAN", label: "> (greater than)", type: "operator" },
    { id: "LESS_THAN", label: "< (less than)", type: "operator" },
    { id: "GREATER_THAN_EQUAL", label: ">= (greater than or equal to)", type: "operator" },
    { id: "LESS_THAN_EQUAL", label: "<= (less than or equal to)", type: "operator" },
    { id: "IS_BLANK", label: "is blank", type: "standard" },
    { id: "IS_NOT_BLANK", label: "is not blank", type: "standard" },
    { id: "IS_ZERO", label: "is zero", type: "standard" },
    { id: "IS_NOT_ZERO", label: "is not zero", type: "standard" },
  ],
  DATE: [
    { id: "EQUALS", label: "Is", type: "operator" },
    { id: "NOT_EQUALS", label: "Is Not", type: "operator" },
    { id: "GREATER_THAN", label: "After", type: "operator" },
    { id: "LESS_THAN", label: "Before", type: "operator" },
  ],
  DROPDOWN: [
    { id: "CONTAINS", label: "contains", type: "operator" },
    { id: "MATCHES_WITH", label: "matches with", type: "operator" },
    { id: "STARTS_WITH", label: "starts with", type: "operator" },
    { id: "IS_BLANK", label: "is blank", type: "standard" },
    { id: "IS_NOT_BLANK", label: "is not blank", type: "standard" },
  ],
};

/**
 * Prefix for synthetic FieldSelection dropdown rows when a group is collapsed (ids are not real variables).
 * @author Abhishek Dhillon
 */
export const FIELD_SELECTION_COLLAPSED_ROW_ID_PREFIX = "__field_selection_collapsed__";

/**
 * Prefix for synthetic ComboBox value-input rows when a variable group is collapsed (not a real option).
 * @author Abhishek Dhillon
 */
export const COMBO_BOX_INPUT_COLLAPSED_ROW_ID_PREFIX = "__combobox_value_collapsed__";

/**
 * Popup list height (px) for the expression value ComboBox — a bounded, scrollable list is required for sticky group headers to track the active group.
 * @author Abhishek Dhillon
 */
export const EXPRESSION_COMBOBOX_POPUP_LIST_HEIGHT_PX = 320;

/**
 * UI labels for expression builder
 * @author Abhishek Dhillon
 */
export const EXPRESSION_LABELS = {
  EXPRESSION_BUILDER: "Expression Builder",
  ADD_RULE: "Add Rule",
  ADD_GROUP: "Add Group",
  FIELD: "Condition",
  OPERATOR: "Operator",
  VALUE: "Condition",
  SELECT_FIELD: "Select Field",
  SELECT_OPERATOR: "Select Operator",
  ENTER_VALUE: "Enter Value",
  AND: "AND",
  OR: "OR",
  GROUP: "Group",
  ROOT_GROUP: "",
  PREVIEW_TITLE: "Expression Preview",
  AVAILABLE_FIELDS: "Available Fields",
  EXPRESSION_JSON: "Expression JSON",
  TEST_EVALUATION: "Test Evaluation",
  NO_VARIABLES_AVAILABLE: "No Variables Available",
  NO_VARIABLES_MESSAGE: "Please add workflow variables first to create expressions.",
  NO_FIELDS_AVAILABLE: "No fields available",
  TEST_DATA: "Test Data",
  RESULT: "Result",
  PASS: "PASS",
  FAIL: "FAIL",
  CLOSE_MODAL: "Close Modal",
  CLOSE: "Close",
  DELETE_RULE: "Delete Rule",
  DELETE_GROUP: "Delete Group",
  TEST_EXPRESSION: "Test Expression",
};

/**
 * Field type hints for user guidance
 * @author Abhishek Dhillon
 */
export const FIELD_TYPE_HINTS = {
  number: "Enter a number",
  date: "Select a date",
  text: "Enter text value",
  dropdown: "Choose from available options",
};

/**
 * Field type badge colors
 * @author Abhishek Dhillon
 */
export const FIELD_TYPE_COLORS = {
  number: "bg-info",
  date: "bg-warning",
  dropdown: "bg-success",
  text: "bg-secondary",
};

export const WF_EXPRESSION_VARIABLE_GROUP_NAMES = {
  TASK_CHIPS: "TASK CHIPS",
  EMAIL_CHIPS: "EMAIL CHIPS",
  FORM_SMARTCHIPS: "FORM SMARTCHIPS",
  CREATOR_CHIPS: "CREATOR CHIPS",
  ASSIGNEE_CHIPS: "ASSIGNEE CHIPS",
  USER_CHIPS: "USER CHIPS",
};

export const WF_EXPRESSION_VARIABLE_GROUP_HEADERS = {
  FORM_SMARTCHIPS: "Chips",
  CREATOR_CHIPS: "Creator Chips",
  ASSIGNEE_CHIPS: "Assignee Chips",
  USER_CHIPS: "User Chips",
};

/**
 * Patterns used when evaluating expression rules (date-only comparison, etc.)
 * @author Abhishek Dhillon
 */
export const EXPRESSION_EVAL = {
  ISO_DATE_ONLY: /^\d{4}-\d{2}-\d{2}$/,
  ISO_DATE_TIME_PREFIX: /^\d{4}-\d{2}-\d{2}T/,
  /** Join separator when coercing array values to a comparable text string */
  ARRAY_VALUE_SEPARATOR: ",",
};
